import { useMemo, useState, useCallback } from 'react';

import { PayoutType } from 'types';
import { useAppSelector } from 'store';
import { Flex } from 'components/flex';
import { Info } from 'components/info';
import { mapQuery } from 'utils/query';
import { Payout } from 'store/api/payout';
import { Row, Col } from 'components/grid';
import { isMobile, isDesktop } from 'hooks';
import { formatPrice } from 'utils/helpers';
import { ListCard } from 'components/list-card';
import { ListHeader } from 'components/list-header';
import { formatTimezoneSpecific } from 'utils/date';
import { selectCanReadMissingSales } from 'store/slices/auth';
import { useReportQuery, useDetailsQuery } from 'store/api/event';
import {
  EventPayment,
  EventProfitAndLossLtlDetail,
} from 'store/api/event/types';

import * as Styles from './styles';
import { LtlDrawer } from './ltl-drawer';
import { BonusesDrawer } from './bonuses-drawer';
import * as LineStyles from './line-check/styles';
import { LocationRentDrawer } from './location-rent-drawer';
import { CategoryDetailsDrawer } from './category-details-drawer';
import { Expense, CardProps, EventProfitAndLossesProps } from './types';
import {
  commonProps,
  revenueTitles,
  expensesTitles,
  getSummaryInfo,
  getExpensesInfo,
  EVENT_MISC_NAME,
  LTL_EXPENSE_NAME,
  expensesTemplate,
  revenueCellsTablet,
  commonFooterCardProps,
  getMissingSalesRevenue,
  calculatePaymentMethods,
  MISSING_SALES_REVENUE_NAME,
} from './config';

const Card = ({ title, children, minHeight, fullHeight = true }: CardProps) => (
  <Flex
    fullWidth
    minHeight={minHeight}
    flexDirection="column"
    fullHeight={fullHeight}
  >
    <h2>{title}</h2>
    {children}
  </Flex>
);

export const EventProfitAndLosses = ({
  event,
  onMissingSales,
  profitAndLosses,
}: EventProfitAndLossesProps) => {
  const canReadMissing = useAppSelector(selectCanReadMissingSales);
  const mobile = isMobile();
  const desktop = isDesktop();
  const [bonuses, setBonuses] = useState<Payout[]>();
  const [locationRent, setLocationRent] = useState<EventPayment[]>();
  const [ltlDetails, setLtlDetails] = useState<EventProfitAndLossLtlDetail[]>();
  const [selectedCategory, setSelectedCategory] = useState<string>();
  // Owner 2026-10-17: the lines that had no drawer get the same drawers.
  const [reimbursements, setReimbursements] = useState<Payout[]>();
  const [eventPaymentsLine, setEventPaymentsLine] = useState<{
    title: string;
    payments: EventPayment[];
  }>();
  // The clicked line's amount: each drawer checks its records add up to it.
  const [lineAmount, setLineAmount] = useState<number>();

  const { data, ...props } = useReportQuery(
    {
      id: String(event?.id),
    },
    { skip: !event || !event?.id }
  );
  // Electricity / COI are event payments; the report dialog already loads
  // the event record (cached), which carries them.
  const { data: eventDetails } = useDetailsQuery(String(event?.id), {
    skip: !event?.id,
  });

  const totalPaymentRevenue = useMemo(
    () => calculatePaymentMethods(data?.report),
    [data?.report]
  );
  // Only for the roles that read Missing sales (the backend leaves them out
  // of the P&L for everyone else).
  const missingRevenue = useMemo(
    () =>
      canReadMissing ? getMissingSalesRevenue(profitAndLosses) : undefined,
    [canReadMissing, profitAndLosses]
  );
  // Total Revenue = the register days + the Missing sales line, per column.
  const revenueTotals = useMemo(() => {
    const cents = (value?: number) => Math.round(Number(value || 0) * 100);
    const add = (...values: (number | undefined)[]) =>
      values.reduce<number>((acc, value) => acc + cents(value), 0) / 100;
    return {
      cash: add(totalPaymentRevenue?.cash, missingRevenue?.cash),
      check: add(totalPaymentRevenue?.check, missingRevenue?.check),
      credit: add(totalPaymentRevenue?.credit, missingRevenue?.credit),
      tip: add(
        ...(data?.report || []).map((value) => value?.total?.tip),
        missingRevenue?.tip
      ),
    };
  }, [data?.report, missingRevenue, totalPaymentRevenue]);

  const {
    summaryInfo,
    bonusesList,
    expensesInfo,
    categoryDetails,
    reimbursementsList,
  } = useMemo(() => {
    const isBonus = (item: Payout) =>
      [PayoutType.Bonus, PayoutType.PrepaidBonus].includes(item.type);
    return {
      summaryInfo: getSummaryInfo(profitAndLosses),
      expensesInfo: getExpensesInfo(profitAndLosses),
      categoryDetails: profitAndLosses?.categoryDetails || [],
      bonusesList: profitAndLosses?.payoutItems?.filter(isBonus) || [],
      // Owner go 2026-10-17: the Reimbursements line no longer counts the
      // bonuses (they are the Bonuses line), so its drawer lists the rest.
      reimbursementsList:
        profitAndLosses?.payoutItems?.filter((item) => !isBonus(item)) || [],
    };
  }, [profitAndLosses]);

  const accountingExpenses = useMemo(
    () =>
      profitAndLosses?.accountingExpensesByCategory?.map((value) => ({
        ...value,
        withCategory: true,
      })) || [],
    [profitAndLosses?.accountingExpensesByCategory]
  );

  const expenses: Expense[] = useMemo(
    () => [...expensesInfo, ...accountingExpenses],
    [expensesInfo, accountingExpenses]
  );

  const eventPaymentsByLine = useMemo(
    () => ({
      COI: (eventDetails?.payments || []).filter(
        (payment) => payment.transaction === 'coi'
      ),
      [EVENT_MISC_NAME]: (eventDetails?.payments || []).filter(
        (payment) => payment.transaction === 'misc'
      ),
      Electricity: (eventDetails?.payments || []).filter(
        (payment) => payment.transaction === 'electricity'
      ),
    }),
    [eventDetails?.payments]
  );

  const handleClick = useCallback(
    (value: Expense) => {
      setLineAmount(value.amount);

      if (value.name === 'Reimbursements') {
        setReimbursements(reimbursementsList);
        return;
      }

      if (
        value.name === 'Electricity' ||
        value.name === 'COI' ||
        value.name === EVENT_MISC_NAME
      ) {
        setEventPaymentsLine({
          title: value.name,
          payments: eventPaymentsByLine[value.name],
        });
        return;
      }

      if (value?.withCategory) {
        setSelectedCategory(value.name);
        return;
      }

      if (value.name === 'Bonuses') {
        setBonuses(bonusesList);
        return;
      }

      if (value.name === 'Locations Rent') {
        setLocationRent(profitAndLosses?.locationRentPayments);
        return;
      }

      if (value.name === LTL_EXPENSE_NAME) {
        setLtlDetails(profitAndLosses?.ltlDetails);
      }
    },
    [
      bonusesList,
      eventPaymentsByLine,
      profitAndLosses?.ltlDetails,
      reimbursementsList,
      profitAndLosses?.locationRentPayments,
    ]
  );

  const expensesList = useMemo(() => {
    // Every line opens only when it has records (no dead clicks).
    const clickableExpensesNames: string[] = [];
    if (bonusesList.length) {
      clickableExpensesNames.push('Bonuses');
    }
    if (profitAndLosses?.locationRentPayments?.length) {
      clickableExpensesNames.push('Locations Rent');
    }
    // LTL opens its rows only when the backend sends them (older backends
    // send only the two totals).
    if (profitAndLosses?.ltlDetails?.length) {
      clickableExpensesNames.push(LTL_EXPENSE_NAME);
    }
    // The lines that had no drawer open one when they have records.
    if (reimbursementsList.length) {
      clickableExpensesNames.push('Reimbursements');
    }
    if (eventPaymentsByLine.Electricity.length) {
      clickableExpensesNames.push('Electricity');
    }
    if (eventPaymentsByLine.COI.length) {
      clickableExpensesNames.push('COI');
    }
    if (eventPaymentsByLine[EVENT_MISC_NAME].length) {
      clickableExpensesNames.push(EVENT_MISC_NAME);
    }

    return expenses?.map((value, index) => {
      const clickable =
        value?.withCategory || clickableExpensesNames.includes(value.name);
      const card = (
        <ListCard
          key={value?.name}
          // Striping by position: a line wrapped for the keyboard is no
          // longer an nth-child of the list.
          isOdd={index % 2 === 0}
          headers={expensesTitles}
          tabletTemplate="1fr 1fr"
          template={expensesTemplate}
          onClick={clickable ? () => handleClick(value) : undefined}
          values={[
            value?.note ? (
              <LineStyles.NamedLine>
                <span>{value.name}</span>
                <LineStyles.LineNote>{value.note}</LineStyles.LineNote>
              </LineStyles.NamedLine>
            ) : (
              value?.name
            ),
            formatPrice(value?.amount),
          ]}
        />
      );
      if (!clickable) {
        return card;
      }
      // Keyboard: Tab to the line, Enter / Space opens its drawer.
      return (
        <LineStyles.LineButton
          tabIndex={0}
          role="button"
          key={value?.name}
          aria-label={`Show the records behind ${value?.name}`}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              handleClick(value);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleClick(value);
            }
          }}
        >
          {card}
        </LineStyles.LineButton>
      );
    });
  }, [
    expenses,
    handleClick,
    eventPaymentsByLine,
    profitAndLosses?.ltlDetails?.length,
    reimbursementsList.length,
    bonusesList.length,
    profitAndLosses?.locationRentPayments?.length,
  ]);

  const expensesFooter = useMemo(
    () => (
      <ListCard
        {...commonFooterCardProps}
        tabletTemplate="1fr 1fr"
        template={expensesTemplate}
        headers={['', 'Total Amount']}
        values={[
          'Total Expenses',
          formatPrice(
            expenses?.reduce((acc, value) => acc + Number(value?.amount), 0)
          ),
        ]}
      />
    ),
    [expenses]
  );

  const revenueList = useMemo(
    () => [
      ...(data?.report?.map((value) => (
        <ListCard
          key={value.date}
          headers={revenueTitles}
          values={[
            formatTimezoneSpecific(value?.checkoutDate, event?.timezone?.code),
            formatPrice(value?.paymentMethods?.check),
            formatPrice(value?.paymentMethods?.cash),
            formatPrice(value?.paymentMethods?.credit),
            formatPrice(value?.total?.tip),
          ]}
        />
      )) || []),
      ...(missingRevenue
        ? [
            <ListCard
              key={MISSING_SALES_REVENUE_NAME}
              isOdd={(data?.report?.length || 0) % 2 === 0}
              headers={[''].concat(revenueTitles.slice(1))}
              cellsTablet={mobile ? undefined : revenueCellsTablet}
              values={[
                MISSING_SALES_REVENUE_NAME,
                formatPrice(missingRevenue.check),
                formatPrice(missingRevenue.cash),
                formatPrice(missingRevenue.credit),
                formatPrice(missingRevenue.tip),
              ]}
            />,
          ]
        : []),
    ],
    [data, event?.timezone?.code, missingRevenue, mobile]
  );

  const revenueFooter = useMemo(
    () => (
      <ListCard
        {...commonFooterCardProps}
        headers={[''].concat(revenueTitles.slice(1))}
        cellsTablet={mobile ? undefined : revenueCellsTablet}
        values={[
          'Total Revenue',
          formatPrice(revenueTotals.check),
          formatPrice(revenueTotals.cash),
          formatPrice(revenueTotals.credit),
          formatPrice(revenueTotals.tip),
        ]}
      />
    ),
    [mobile, revenueTotals]
  );

  return (
    <>
      <Flex
        gap={16}
        fullWidth
        fullHeight
        flexDirection={!desktop ? 'column' : 'row'}
      >
        <Flex column gap={16} fullWidth>
          {canReadMissing && !!profitAndLosses?.missingSalesRowCount && (
            <p>
              Includes missing sales{' '}
              {formatPrice(profitAndLosses.totalMissingSalesGross)} · Tax{' '}
              {formatPrice(profitAndLosses.totalMissingSalesTax)} · Tips{' '}
              {formatPrice(profitAndLosses.totalMissingSalesTip)}. These are
              separate from register sales.{' '}
              {onMissingSales && (
                <button type="button" onClick={onMissingSales}>
                  View Missing sales
                </button>
              )}
            </p>
          )}
          <Card title="Summary" fullHeight={false}>
            <Row>
              {summaryInfo?.map((value, i) => (
                <Col col={2} key={i} colTablet={4} colMobile={6}>
                  <Info {...value} />
                </Col>
              ))}
            </Row>
          </Card>
          <Card minHeight={0} title="Revenue">
            <Styles.StyledList
              {...commonProps}
              {...mapQuery(props)}
              footer={revenueFooter}
              header={<ListHeader padding="8px" titles={revenueTitles} />}
              count={(data?.report?.length || 0) + (missingRevenue ? 1 : 0)}
            >
              {revenueList}
            </Styles.StyledList>
          </Card>
        </Flex>

        <Card title="Expenses">
          <Styles.StyledList
            $isExpenses
            {...commonProps}
            footer={expensesFooter}
            count={expenses?.length}
            header={
              <ListHeader
                padding="8px"
                titles={expensesTitles}
                template={expensesTemplate}
              />
            }
          >
            {expensesList}
          </Styles.StyledList>
        </Card>
      </Flex>

      <CategoryDetailsDrawer
        eventName={event?.name}
        lineAmount={lineAmount}
        open={!!selectedCategory}
        categoryName={selectedCategory}
        categoryDetails={categoryDetails}
        onClose={() => setSelectedCategory(undefined)}
      />

      <BonusesDrawer
        bonuses={bonuses}
        eventName={event?.name}
        lineAmount={lineAmount}
        open={!!bonuses?.length}
        onClose={() => setBonuses(undefined)}
      />

      <BonusesDrawer
        title="Reimbursements"
        eventName={event?.name}
        lineAmount={lineAmount}
        bonuses={reimbursements}
        open={!!reimbursements?.length}
        onClose={() => setReimbursements(undefined)}
      />

      <LocationRentDrawer
        eventName={event?.name}
        lineAmount={lineAmount}
        opened={!!locationRent}
        locationRent={locationRent}
        onClose={() => setLocationRent(undefined)}
      />

      <LocationRentDrawer
        eventName={event?.name}
        lineAmount={lineAmount}
        opened={!!eventPaymentsLine}
        title={eventPaymentsLine?.title}
        locationRent={eventPaymentsLine?.payments}
        onClose={() => setEventPaymentsLine(undefined)}
      />

      <LtlDrawer
        opened={!!ltlDetails}
        eventName={event?.name}
        lineAmount={lineAmount}
        ltlDetails={ltlDetails}
        onClose={() => setLtlDetails(undefined)}
      />
    </>
  );
};
